// Sample COMMB-member OOH locations.
//
// COMMB (the Canadian Out-of-Home Marketing and Measurement Bureau) licenses
// its measured-inventory database to members; there is no free public API.
// This bundled dataset is a REPRESENTATIVE SAMPLE of typical COMMB-member
// operator inventory in major Canadian markets (approximate coordinates at
// well-known intersections and corridors). It is used as a fallback so the
// COMMB map layer works out of the box. To use real licensed data, import a
// CSV on the Inventory Map page — imported rows are stored in the
// `CommbLocations` Firestore collection and replace this sample.

export interface CommbLocation {
  id: string;
  operatorCompany: string;
  assetType: string;
  latitude: number;
  longitude: number;
  address: string;
  market: string;
  isSample?: boolean;
}

const sample = (
  id: string,
  operatorCompany: string,
  assetType: string,
  latitude: number,
  longitude: number,
  address: string,
  market: string
): CommbLocation => ({
  id,
  operatorCompany,
  assetType,
  latitude,
  longitude,
  address,
  market,
  isSample: true,
});

export const COMMB_SAMPLE_LOCATIONS: CommbLocation[] = [
  // --- Toronto / GTA ---
  sample('commb-to-001', 'Branded Cities', 'Digital Spectacular', 43.6561, -79.3802, 'Yonge-Dundas Square', 'Toronto'),
  sample('commb-to-002', 'OUTFRONT Media', 'Digital Billboard', 43.6393, -79.4230, 'Gardiner Expressway & Dufferin St', 'Toronto'),
  sample('commb-to-003', 'Pattison Outdoor', 'Horizontal Poster', 43.6677, -79.3948, 'Bloor St W & Spadina Ave', 'Toronto'),
  sample('commb-to-004', 'Astral Out-of-Home', 'Transit Shelter', 43.6487, -79.3975, 'Queen St W & Bathurst St', 'Toronto'),
  sample('commb-to-005', 'Pattison Outdoor', 'Superboard', 43.7130, -79.3446, 'Hwy 401 & Leslie St', 'Toronto'),
  sample('commb-to-006', 'OUTFRONT Media', 'Billboard', 43.7057, -79.3982, 'Hwy 401 & Allen Rd', 'Toronto'),
  sample('commb-to-007', 'Astral Out-of-Home', 'Digital Billboard', 43.6435, -79.3790, 'Gardiner Expressway & Spadina Ave', 'Toronto'),
  sample('commb-to-008', 'Branded Cities', 'Digital Spectacular', 43.6426, -79.3871, 'Rogers Centre district, Front St W', 'Toronto'),
  sample('commb-to-009', 'Pattison Outdoor', 'Transit Shelter', 43.6544, -79.3807, 'Yonge St & Queen St', 'Toronto'),
  sample('commb-to-010', 'OUTFRONT Media', 'Mural', 43.6479, -79.3963, 'Queen St W & Portland St', 'Toronto'),
  sample('commb-to-011', 'Pattison Outdoor', 'Digital Billboard', 43.7764, -79.3468, 'Hwy 404 & Sheppard Ave E', 'Toronto'),
  sample('commb-to-012', 'Astral Out-of-Home', 'Vertical Poster', 43.6629, -79.3957, 'Spadina Ave & College St', 'Toronto'),
  sample('commb-to-013', 'OUTFRONT Media', 'Billboard', 43.5951, -79.6437, 'QEW & Hurontario St', 'Mississauga'),
  sample('commb-to-014', 'Pattison Outdoor', 'Digital Billboard', 43.7315, -79.7624, 'Hwy 410 & Queen St E', 'Brampton'),
  sample('commb-to-015', 'Pattison Outdoor', 'Billboard', 43.8561, -79.5085, 'Hwy 400 & Rutherford Rd', 'Vaughan'),

  // --- Montreal ---
  sample('commb-mtl-001', 'Astral Out-of-Home', 'Digital Spectacular', 45.5088, -73.5678, 'Ste-Catherine St & McGill College Ave', 'Montreal'),
  sample('commb-mtl-002', 'Pattison Outdoor', 'Billboard', 45.5231, -73.5817, 'Boul. St-Laurent & Rue Sherbrooke', 'Montreal'),
  sample('commb-mtl-003', 'OUTFRONT Media', 'Digital Billboard', 45.4972, -73.5794, 'Autoroute Ville-Marie & Rue Guy', 'Montreal'),
  sample('commb-mtl-004', 'Astral Out-of-Home', 'Transit Shelter', 45.5017, -73.5673, 'René-Lévesque Blvd & Rue Peel', 'Montreal'),
  sample('commb-mtl-005', 'Quebecor Out of Home', 'Digital Billboard', 45.5579, -73.5515, 'Autoroute 40 & Boul. Pie-IX', 'Montreal'),
  sample('commb-mtl-006', 'Astral Out-of-Home', 'Vertical Poster', 45.5169, -73.5622, 'Rue St-Denis & Rue Ontario', 'Montreal'),
  sample('commb-mtl-007', 'Pattison Outdoor', 'Superboard', 45.4656, -73.6035, 'Autoroute Décarie & Rue Jean-Talon', 'Montreal'),

  // --- Vancouver ---
  sample('commb-van-001', 'Pattison Outdoor', 'Digital Spectacular', 49.2820, -123.1171, 'Robson St & Burrard St', 'Vancouver'),
  sample('commb-van-002', 'OUTFRONT Media', 'Billboard', 49.2664, -123.1155, 'Cambie St & W Broadway', 'Vancouver'),
  sample('commb-van-003', 'Pattison Outdoor', 'Transit Shelter', 49.2827, -123.1207, 'W Georgia St & Granville St', 'Vancouver'),
  sample('commb-van-004', 'Lamar Advertising', 'Digital Billboard', 49.2632, -123.0693, 'Kingsway & Victoria Dr', 'Vancouver'),
  sample('commb-van-005', 'Pattison Outdoor', 'Billboard', 49.2110, -123.0071, 'Trans-Canada Hwy & Willingdon Ave', 'Burnaby'),
  sample('commb-van-006', 'OUTFRONT Media', 'Digital Billboard', 49.1937, -122.8508, 'King George Blvd & 104 Ave', 'Surrey'),

  // --- Calgary ---
  sample('commb-cal-001', 'Pattison Outdoor', 'Digital Billboard', 51.0447, -114.0719, '8 Ave SW & Centre St (Stephen Ave)', 'Calgary'),
  sample('commb-cal-002', 'OUTFRONT Media', 'Billboard', 51.0577, -114.0855, '16 Ave NW & 10 St NW (Trans-Canada Hwy)', 'Calgary'),
  sample('commb-cal-003', 'Lamar Advertising', 'Digital Billboard', 51.0272, -114.0535, 'Macleod Trail & 25 Ave SE', 'Calgary'),
  sample('commb-cal-004', 'Pattison Outdoor', 'Transit Shelter', 51.0486, -114.0637, '7 Ave SW transit corridor', 'Calgary'),

  // --- Edmonton ---
  sample('commb-edm-001', 'Pattison Outdoor', 'Digital Billboard', 53.5461, -113.4938, 'Jasper Ave & 101 St', 'Edmonton'),
  sample('commb-edm-002', 'OUTFRONT Media', 'Billboard', 53.5225, -113.5063, 'Whyte Ave & Gateway Blvd', 'Edmonton'),
  sample('commb-edm-003', 'Lamar Advertising', 'Billboard', 53.5720, -113.5065, 'Yellowhead Trail & 97 St', 'Edmonton'),

  // --- Ottawa ---
  sample('commb-ott-001', 'Pattison Outdoor', 'Digital Billboard', 45.4215, -75.6972, 'Rideau St & Sussex Dr', 'Ottawa'),
  sample('commb-ott-002', 'Astral Out-of-Home', 'Transit Shelter', 45.4111, -75.6981, 'Bank St & Somerset St W', 'Ottawa'),
  sample('commb-ott-003', 'OUTFRONT Media', 'Billboard', 45.3488, -75.7597, 'Hwy 417 & Maitland Ave', 'Ottawa'),

  // --- Winnipeg ---
  sample('commb-wpg-001', 'Pattison Outdoor', 'Digital Billboard', 49.8951, -97.1384, 'Portage Ave & Main St', 'Winnipeg'),
  sample('commb-wpg-002', 'OUTFRONT Media', 'Billboard', 49.8801, -97.2077, 'Portage Ave & St James St', 'Winnipeg'),

  // --- Halifax ---
  sample('commb-hfx-001', 'Pattison Outdoor', 'Billboard', 44.6488, -63.5752, 'Barrington St & Spring Garden Rd', 'Halifax'),
  sample('commb-hfx-002', 'OUTFRONT Media', 'Digital Billboard', 44.6702, -63.6100, 'Bayers Rd & Connaught Ave', 'Halifax'),

  // --- Quebec City ---
  sample('commb-qc-001', 'Quebecor Out of Home', 'Digital Billboard', 46.8139, -71.2080, 'Grande Allée & Ave Honoré-Mercier', 'Quebec City'),
  sample('commb-qc-002', 'Astral Out-of-Home', 'Transit Shelter', 46.8033, -71.2430, 'Boul. Laurier & Route de l’Église', 'Quebec City'),
];
